<template>
  <div>
    <hr>
    <div class="d-flex gap-2">
      <button class="btn btn-primary" @click="generateXML" :disabled="!factura">
        Generar XML
      </button>
      <button class="btn btn-success" @click="enviarSRI" :disabled="!xmlContent">
        Enviar al SRI
      </button>
    </div>
    <div v-if="error" class="alert alert-danger mt-2">{{ error }}</div>
    <textarea v-model="xmlContent" rows="14"
              class="form-control mt-3 font-monospace"
              style="font-size:12px"></textarea>
  </div>
</template>
<script>
import api from "@/services/api";
import {
  formatFecha,
  calcularClaveAcceso,
  sanitizeXml,
  getTipoIdentificacion,
  calcularIVA,
  padSecuencial
} from "@/utils/sri";

export default {
  props: {
    p_idFactura: { type: Number, required: true }
  },
  data() {
    return {
      factura: null,
      empresa: null,
      detalles: [],
      xmlContent: '',
      error: null,
      // Configuración — idealmente desde variables de entorno
      AMBIENTE: '1',       // 1=pruebas, 2=producción
      TIPO_EMISION: '1',   // 1=normal
      IVA_PORCENTAJE: 15,  // Ecuador 2024: 15%
      CODIGO_IVA: '2',     // código SRI para IVA
      CODIGO_PORCENTAJE_IVA: '4', // 15% → código 4 en SRI
    };
  },
  methods: {
    fetchEmpresa() {
      return api.get('/vwempresas/' + this.factura.idEmpresa)
        .then(r => { this.empresa = r.data; })
        .catch(e => console.error('Error fetching empresa:', e));
    },
    fetchFactura() {
      api.get('/vwfacturas/' + this.p_idFactura)
        .then(r => {
          this.factura = r.data;
          return this.fetchEmpresa();
        })
        .then(() => this.fetchDetalles())
        .catch(e => { this.error = 'Error cargando datos'; console.error(e); });
    },
    fetchDetalles() {
      api.get('/vwfacturadets/idfactura/' + this.p_idFactura)
        .then(r => { this.detalles = r.data; this.generateXML(); })
        .catch(e => { this.error = 'Error cargando detalles'; console.error(e); });
    },

    generateXML() {
      if (!this.factura || !this.detalles.length) return;
      this.error = null;
      try {
        const f          = this.factura;
        const em         = this.empresa;
        const ruc        = em.documento;                  // RUC de empresa
        const secuencial = padSecuencial(f.numero);       // 9 dígitos con ceros
        const fecha      = formatFecha(f.fechaEmision);   // dd/MM/yyyy

        // Totales
        const totalSinImpuestos = this.detalles
          .reduce((a, d) => a + (d.subtotal || 0), 0);
        const totalDescuento = this.detalles
          .reduce((a, d) => a + (d.descuentoMonto || 0), 0);
        const baseIva    = totalSinImpuestos;
        const valorIva   = calcularIVA(baseIva, this.IVA_PORCENTAJE);
        const importeTotal = (baseIva + valorIva).toFixed(2);

        // Clave de acceso (49 dígitos)
        const claveAcceso = calcularClaveAcceso({
          fecha, codDoc: '01', ruc,
          ambiente: this.AMBIENTE,
          estab: '001', ptoEmi: '001',
          secuencial, tipoEmision: this.TIPO_EMISION
        });

        const detallesXml = this.detalles.map(det => {
          const subtotal     = Number(det.subtotal).toFixed(2);
          const descuento    = Number(det.descuentoMonto || 0).toFixed(2);
          const valorIvaDet  = calcularIVA(Number(det.subtotal), this.IVA_PORCENTAJE);
          return `
        <detalle>
          <codigoPrincipal>${sanitizeXml(String(det.idFacturaDet))}</codigoPrincipal>
          <descripcion>${sanitizeXml(det.descripcion)}</descripcion>
          <cantidad>${Number(det.cantidad).toFixed(2)}</cantidad>
          <precioUnitario>${Number(det.precioUnitario).toFixed(6)}</precioUnitario>
          <descuento>${descuento}</descuento>
          <precioTotalSinImpuesto>${subtotal}</precioTotalSinImpuesto>
          <impuestos>
            <impuesto>
              <codigo>${this.CODIGO_IVA}</codigo>
              <codigoPorcentaje>${this.CODIGO_PORCENTAJE_IVA}</codigoPorcentaje>
              <tarifa>${this.IVA_PORCENTAJE}</tarifa>
              <baseImponible>${subtotal}</baseImponible>
              <valor>${valorIvaDet}</valor>
            </impuesto>
          </impuestos>
        </detalle>`;
        }).join('');

        this.xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<factura id="comprobante" version="1.1.0">
  <infoTributaria>
    <ambiente>${this.AMBIENTE}</ambiente>
    <tipoEmision>${this.TIPO_EMISION}</tipoEmision>
    <razonSocial>${sanitizeXml(em.razonSocial)}</razonSocial>
    <nombreComercial>${sanitizeXml(em.nombreCorto)}</nombreComercial>
    <ruc>${em.documento}</ruc>
    <claveAcceso>${claveAcceso}</claveAcceso>
    <codDoc>01</codDoc>
    <estab>001</estab>
    <ptoEmi>001</ptoEmi>
    <secuencial>${secuencial}</secuencial>
    <dirMatriz>${sanitizeXml(em.direccion)}</dirMatriz>
  </infoTributaria>
  <infoFactura>
    <fechaEmision>${fecha}</fechaEmision>
    <dirEstablecimiento>${sanitizeXml(f.empresaDireccion || '')}</dirEstablecimiento>
    <obligadoContabilidad>SI</obligadoContabilidad>
    <tipoIdentificacionComprador>${getTipoIdentificacion(f.personaDocumento)}</tipoIdentificacionComprador>
    <razonSocialComprador>${sanitizeXml(f.personaNombre)}</razonSocialComprador>
    <identificacionComprador>${f.personaDocumento}</identificacionComprador>
    <totalSinImpuestos>${totalSinImpuestos.toFixed(2)}</totalSinImpuestos>
    <totalDescuento>${totalDescuento.toFixed(2)}</totalDescuento>
    <totalConImpuestos>
      <totalImpuesto>
        <codigo>${this.CODIGO_IVA}</codigo>
        <codigoPorcentaje>${this.CODIGO_PORCENTAJE_IVA}</codigoPorcentaje>
        <baseImponible>${baseIva.toFixed(2)}</baseImponible>
        <valor>${valorIva}</valor>
      </totalImpuesto>
    </totalConImpuestos>
    <propina>0.00</propina>
    <importeTotal>${importeTotal}</importeTotal>
    <moneda>DOLAR</moneda>
    <pagos>
      <pago>
        <formaPago>01</formaPago>
        <total>${importeTotal}</total>
        <plazo>0</plazo>
        <unidadTiempo>dias</unidadTiempo>
      </pago>
    </pagos>
  </infoFactura>
  <detalles>${detallesXml}
  </detalles>
  <infoAdicional>
    <campoAdicional nombre="Direccion">${sanitizeXml(f.personaDireccion || 'N/A')}</campoAdicional>
    <campoAdicional nombre="Telefono">${sanitizeXml(f.personaTelefono || 'N/A')}</campoAdicional>
    <campoAdicional nombre="Email">${sanitizeXml(f.personaEmail || 'N/A')}</campoAdicional>
  </infoAdicional>
</factura>`;
      } catch (err) {
        this.error = 'Error generando XML: ' + err.message;
        console.error(err);
      }
    },

    enviarSRI() {
      // Aquí va la llamada a tu backend para firma y envío al SRI
      api.post('/sri/enviar', { xml: this.xmlContent, idFactura: this.p_idFactura })
        .then(r => console.log('Respuesta SRI:', r.data))
        .catch(e => { this.error = 'Error al enviar'; console.error(e); });
    }
  },
  mounted() { this.fetchFactura(); }
};
</script>